/**
 * ResearchPanel — form + job history for Deep Search tab.
 *
 * Job results are displayed on a separate page: /research/{jobId}
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from '../config/firebase';
import { getImpersonationHeaders } from '../services/impersonationHeaders';
import SearchFiltersComponent from './SearchFilters';
import type { Corpus, SearchFilterOptions, SearchModeValue } from '../types';
import {
  DEFAULT_FILTERS,
  normalizeSbaDocTypes,
  regionsToBackendRegion,
  isUsFdaVisible,
  isUsReviewScope,
} from '../constants/filterConstants';
import { openInNewTab } from '../utils/openInNewTab';
import { coerceSearchMode } from '../utils/coerceSearchMode';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { useHasLabelNegotiations } from '../hooks/useHasLabelNegotiations';
import BrandMark from './BrandMark';

// Allowed values for the ``?researchSearchMode=`` URL param (Finding 1
// fixup-2 after T3+T4).  Module-scope so we don't rebuild on every
// render.  Mirrors ``SearchModeValue``; if the union grows, add the new
// value here too.  ResearchPanel uses a DEDICATED key (not the
// page-level ``?searchMode=`` which is Cogni-owned, and not
// ``?kwSearchMode=`` which is keyword-owned) — three surfaces, three
// dedicated params.  Sharing one would let surfaces clobber each
// other's state on cross-tab navigation.
const VALID_RESEARCH_SEARCH_MODES: ReadonlySet<string> = new Set<SearchModeValue>([
  'labels',
  'draft_review',
  'multi_review',
  'fda_letters',
  'label_negotiations',
  'ci',
  'approval_letters',
]);

const API_URL = import.meta.env['VITE_API_URL'] ||
  'https://us-central1-label-doctor-f255e.cloudfunctions.net/apiProxy';

interface ResearchJob {
  id: string;
  query: string;
  max_results: number;
  status: string;
  progress_pct: number;
  progress_message: string | null;
  drugs_found: number;
  drugs_analyzed: number;
  drugs_total: number;
  // Letter-corpus fields (Task 15).  Optional because legacy / drug jobs
  // do not populate them, and because the backend mirrors ``drugs_*`` into
  // ``letters_*`` only when ``corpus == 'fda_letters'`` (see
  // ``_job_to_response`` in ``backend/app/api/research_routes.py``).
  //
  // ``'ci'`` (Plan-4 Task 5, competitive-intel): NOT mirrored into a
  // dedicated counter pair the way letters are — a ci job reuses the
  // shared ``drugs_found``/``drugs_analyzed``/``drugs_total`` columns
  // directly (see ``ci_research_service.py``), so the history-card render
  // below reads those same fields for ci with a "document(s)" noun instead
  // of the drug-shaped "candidates"/"drug match(es)" copy.
  corpus?: Corpus;
  letters_found?: number | null;
  letters_analyzed?: number | null;
  letters_total?: number | null;
  summary: string | null;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function statusBadge(status: string) {
  const styles: Record<string, string> = {
    pending: 'bg-amber-100 text-amber-700',
    running: 'bg-blue-100 text-blue-700',
    analyzing: 'bg-blue-100 text-blue-700',
    complete: 'bg-green-100 text-green-700',
    error: 'bg-red-100 text-red-700',
    cancelled: 'bg-slate-100 text-slate-500',
    resuming: 'bg-amber-100 text-amber-700',
  };
  const label = ['running', 'analyzing'].includes(status) ? 'running' : status;
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-slate-100 text-slate-500'}`}>
      {label}
    </span>
  );
}

/**
 * Corpus values the panel can dispatch to `POST /v2/research`. Alias of the
 * shared `Corpus` type (not a separately-declared duplicate) — the two are
 * now member-identical (Plan-4 Task 5 added 'ci' to both), so aliasing
 * keeps them from silently drifting apart if either grows again.
 */
type SubmitCorpus = Corpus;

/**
 * Pure helper: maps a searchMode value to the corpus string sent to the
 * backend.  Exported so it can be unit-tested without mounting the full
 * component (Task 8).
 */
export function deriveSubmitCorpus(searchMode: string): SubmitCorpus {
  if (searchMode === 'approval_letters') return 'approval_letters';
  if (searchMode === 'fda_letters') return 'fda_letters';
  if (searchMode === 'multi_review') return 'multi_review';
  if (searchMode === 'label_negotiations') return 'label_negotiations';
  if (searchMode === 'ci') return 'ci';
  return 'labels';
}

/**
 * Per-corpus default for the Max Results dropdown.  'ci' defaults wider
 * (20) than the label corpus (3) — same rationale as fda_letters (10):
 * CI retrieval is cheap grouped-chunk search, not a per-drug LLM
 * evaluator, so recall benefits from a larger default pool.
 */
function defaultMaxResultsForMode(mode: SearchModeValue): number {
  if (mode === 'fda_letters' || mode === 'approval_letters') return 10;
  if (mode === 'ci') return 20;
  return 3;
}

export default function ResearchPanel() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // B3: admin status used for coerceSearchMode in URL-sync coercions.
  const isAdmin = useIsAdmin();
  // Tenant owns ≥1 draft — gates the Investigator ``label_negotiations`` mode
  // in the coercions below, so a stale URL cannot submit a drafts-only
  // research job for a tenant with nothing to search.
  const hasLabelNegotiations = useHasLabelNegotiations();

  // Seed ``filters.searchMode`` from the URL on mount so the
  // "Back to Deep Search" link on ``ResearchResultPage`` (which carries
  // ``?researchSearchMode=fda_letters`` when the result is a letter job)
  // reopens the panel in the right corpus.  Without this seed,
  // ``filters`` would always init to ``DEFAULT_FILTERS`` and the back
  // link's corpus param would be silently dropped.  Validates against
  // ``VALID_RESEARCH_SEARCH_MODES`` to defend against junk URLs.
  const urlResearchSearchMode = searchParams.get('researchSearchMode');
  const initialSearchMode: SearchModeValue =
    urlResearchSearchMode && VALID_RESEARCH_SEARCH_MODES.has(urlResearchSearchMode)
      ? (urlResearchSearchMode as SearchModeValue)
      : DEFAULT_FILTERS.searchMode;
  const initialSbaDocTypes = normalizeSbaDocTypes(
    searchParams.get('researchSbaDocTypes')?.split(','),
  );

  // ``?rq=`` prefills the criterion when CEPI hands a question over
  // ("Open Investigator" on a capability card). Read ONCE, at mount, on
  // purpose: after that the box belongs to the user, and re-reading it in an
  // effect would stomp their edits every time any other URL param changed.
  //
  // A dedicated key rather than ``?q=``, which is Cogni's — sharing it would
  // make a Cogni deep link quietly populate this box too. Nothing is
  // submitted: the user still picks Document Type / Region / Application Type
  // and presses Start Deep Search, which is the whole point of "Open" rather
  // than "Run it for me".
  const [query, setQuery] = useState(() => searchParams.get('rq') ?? '');
  // FDA letter research jobs default to a wider pool (10) than label
  // research (3): letter corpus is smaller per-drug, and the linkage
  // is sparse, so showing more results keeps recall useful at investigator
  // launch.  Users can adjust the value manually after mount.
  const [maxResults, setMaxResults] = useState(
    defaultMaxResultsForMode(initialSearchMode),
  );
  const [filters, setFilters] = useState<SearchFilterOptions>({
    ...DEFAULT_FILTERS,
    searchMode: initialSearchMode,
    sbaDocTypes: initialSbaDocTypes,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notifyMe, setNotifyMe] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // ─── URL ↔ filters.searchMode round-trip ───────────────────────────
  //
  // The Deep Search panel needs LIVE bidirectional sync between the
  // Document Type pill and the URL — every pill click must update the
  // URL, and every URL change (back/forward) must update the pill.
  // This is intentionally different from the keyword tab (which writes
  // on submit only, because its kwConfig URL represents the latest
  // *submitted* search).  ResearchPanel has no "submit shape" to cache
  // between pill click and submit, so the natural round-trip is live.
  //
  // Implementation choice: split the directions.
  //   - URL → state runs as a ``useEffect`` watching ``searchParams``
  //     (no other way to react to back/forward navigation).
  //   - state → URL is folded into the ``handleFiltersChange``
  //     callback the panel hands to ``<SearchFilters onFiltersChange>``
  //     — we know exactly when the user mutates ``filters.searchMode``,
  //     so we write the URL in the same callback rather than via an
  //     effect.  This avoids the classic effect-loop trap where a
  //     state→URL effect plus a URL→state effect chase each other
  //     (every setSearchParams creates a new ``searchParams`` reference
  //     which retriggers the URL→state effect — even functional-setter
  //     bailouts don't always stop the cascade in practice).

  // URL → state: react to ``?researchSearchMode`` changes after mount
  // (browser back/forward nav, or the back link from
  // ResearchResultPage rendering inside the same memory router).
  // ``useSearchParams`` memoizes searchParams on ``location.search``,
  // so this effect only fires when the URL actually changes.  The
  // functional ``setFilters`` updater short-circuits when the value
  // is already correct (React bails out re-renders when the updater
  // returns the same reference) — so a same-value URL change doesn't
  // cause cascading work.
  useEffect(() => {
    const urlValue = searchParams.get('researchSearchMode');
    const validated: SearchModeValue =
      urlValue && VALID_RESEARCH_SEARCH_MODES.has(urlValue)
        ? (urlValue as SearchModeValue)
        : DEFAULT_FILTERS.searchMode;
    setFilters((prev) => {
      // B3: coerce the URL-restored mode for the investigator surface.
      // draft_review is never valid on investigator, and multi_review is
      // admin-only (US/EU/JP), so a stale ?researchSearchMode= URL downgrades
      // to 'labels' for anyone who cannot actually use the mode.
      const coerced = coerceSearchMode(
        { ...prev, searchMode: validated },
        'investigator',
        isAdmin,
        hasLabelNegotiations,
      );
      const restoredSbaDocTypes = normalizeSbaDocTypes(
        searchParams.get('researchSbaDocTypes')?.split(','),
      );
      const sameSbaDocTypes =
        normalizeSbaDocTypes(prev.sbaDocTypes).join(',')
        === restoredSbaDocTypes.join(',');
      if (
        prev.searchMode === coerced.searchMode
        && sameSbaDocTypes
      ) return prev;
      // Mirror the per-corpus ``maxResults`` reset from
      // handleFiltersChange so URL-driven mode changes (browser
      // back/forward, or the back link from ResearchResultPage) also
      // rebase the dropdown to the new corpus's default.  Without this,
      // a value of 10 chosen on letters would persist when the user
      // navigates back to labels, then be submitted as a wider-than-
      // intended request.  ``setMaxResults`` is a state setter — React
      // bails out when the new value equals the current value.
      setMaxResults(defaultMaxResultsForMode(coerced.searchMode));
      return {
        ...coerced,
        sbaDocTypes: restoredSbaDocTypes,
      };
    });
    // ``hasLabelNegotiations`` arrives asynchronously from /auth/sync just like
    // ``isAdmin``; without it here, a first-render downgrade of a saved
    // ?researchSearchMode=label_negotiations URL would never be repaired.
  }, [searchParams, isAdmin, hasLabelNegotiations]);

  // state → URL: wrap ``setFilters`` so any caller-driven mutation of
  // ``filters.searchMode`` is mirrored into the URL.  Default value
  // (``'labels'``) is stripped — canonical "no special mode" has no
  // param.  Uses functional ``setSearchParams(prev => …)`` so other
  // URL params (e.g. ``?mode=researcher``) are preserved.
  //
  // Loop safety: this writes only when the new value differs from the
  // current URL value (same-value writes would still cause navigate()
  // → new searchParams reference → URL→state effect fires, and while
  // that effect's bailout breaks the cycle, skipping the no-op write
  // is cheaper and easier to reason about).
  const handleFiltersChange = useCallback((next: SearchFilterOptions) => {
    setFilters((prev) => {
      // When the user flips Document Type (labels ↔ fda_letters) reset
      // maxResults to the per-corpus default so the selected value is
      // always in the corpus's option set (labels = [1,3,5];
      // letters = [3,5,10,20]).  Without this clamp, a value of 10
      // chosen on letters would fall through to the first valid option
      // when the user switches back to labels (a confusing silent reset).
      if (prev.searchMode !== next.searchMode) {
        setMaxResults(defaultMaxResultsForMode(next.searchMode));
      }
      return next;
    });
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        const currentUrlValue = params.get('researchSearchMode');
        const desired =
          next.searchMode !== DEFAULT_FILTERS.searchMode ? next.searchMode : null;
        const desiredSbaDocTypes = (
          next.searchMode === 'multi_review'
          && next.regions.length === 1
          && next.regions[0] === 'US'
        )
          ? normalizeSbaDocTypes(next.sbaDocTypes).join(',')
          : null;
        if (
          (currentUrlValue ?? null) === desired
          && (params.get('researchSbaDocTypes') ?? null)
            === desiredSbaDocTypes
        ) return prev;
        if (desired === null) {
          params.delete('researchSearchMode');
        } else {
          params.set('researchSearchMode', desired);
        }
        if (desiredSbaDocTypes !== null) {
          params.set('researchSbaDocTypes', desiredSbaDocTypes);
        } else {
          params.delete('researchSbaDocTypes');
        }
        return params;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // Per-surface corpus derivation (Task 3): Document Type lives on
  // ``filters.searchMode`` (the panel's own local state).  The POST body
  // and the inner SearchFilters both consume the derived corpus, so the
  // pill in the panel actually drives the dispatch — previously the
  // ``corpus`` prop fed in from SearchPage came from a separate
  // ``?corpus=`` URL toggle and the in-panel pill mutated only local
  // ``filters.searchMode`` without affecting the POST.
  const panelCorpus: SubmitCorpus = deriveSubmitCorpus(filters.searchMode);

  // Job history
  const [jobs, setJobs] = useState<ResearchJob[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(true);

  // Fetch job history
  const fetchJobs = useCallback(async () => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_URL}/v2/research?limit=20`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...getImpersonationHeaders(),
        },
      });
      if (res.ok) {
        setJobs(await res.json());
      }
    } catch {
      // Silently fail
    } finally {
      setLoadingJobs(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, 15000);
    return () => clearInterval(interval);
  }, [fetchJobs]);

  // Submit
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim() || query.trim().length < 10) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const token = await auth.currentUser?.getIdToken();
      // B3: coerce investigator searchMode at submit time in case a stale
      // mode slipped through (draft_review → labels). For multi_review the
      // outcome depends on the viewer: an ADMIN on US/EU/JP submits
      // corpus 'multi_review' as intended; a NON-ADMIN following the same
      // ?researchSearchMode=multi_review deep-link submits 'labels'.
      // ``hasLabelNegotiations`` adds the same treatment for a stale
      // ?researchSearchMode=label_negotiations from a tenant with no drafts.
      //
      // This is DEFENCE IN DEPTH and is deliberately not independently
      // covered: the URL→state effect above already coerces on every
      // searchParams/isAdmin/hasLabelNegotiations change, so `filters` cannot
      // still hold an invalid mode by the time we get here. Verified
      // 2026-08-16 by mutation — replacing this call with raw `filters`
      // leaves ResearchPanel.sbaDocTypes.test.tsx GREEN. Keep the call
      // anyway: it is the backstop if that effect ever regresses.
      const coercedFilters = coerceSearchMode(
        filters, 'investigator', isAdmin, hasLabelNegotiations,
      );
      const submitCorpus: SubmitCorpus =
        deriveSubmitCorpus(coercedFilters.searchMode);
      const res = await fetch(`${API_URL}/v2/research`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...getImpersonationHeaders(),
        },
        body: JSON.stringify({
          query: query.trim(),
          max_results: maxResults,
          marketing_categories: coercedFilters.marketingCategories,
          // 'ci' (competitive intel) is region-agnostic — the backend
          // 422s corpus='ci' if EITHER ``region`` or ``regions`` is
          // non-empty (see research_routes.py's
          // ``_validate_corpus_region``). Both fields are omitted
          // entirely for ci, regardless of what's selected in
          // ``coercedFilters.regions`` (SearchFilters still renders the
          // region pills for every corpus except fda_letters, so a user
          // may well have a region checked while ci is the active mode).
          ...(submitCorpus === 'ci'
            ? {}
            : submitCorpus === 'approval_letters'
              ? { regions: [isUsFdaVisible(isAdmin) ? 'US_FDA' : 'US'] }
            : {
                region: (submitCorpus === 'multi_review'
                  && coercedFilters.regions.length === 0
                  && isUsReviewScope(coercedFilters.regions, isAdmin))
                  ? 'US_FDA'
                  : regionsToBackendRegion(coercedFilters.regions) ?? null,
                // Pass full multi-region list so backend can apply it via the
                // new ``regions`` field added to StartResearchRequest. Without
                // this, selecting 2+ regions silently collapses to no filter
                // (``regionsToBackendRegion`` returns undefined for multi).
                ...(coercedFilters.regions.length > 0 ? { regions: coercedFilters.regions } : {}),
              }),
          // Phase I Task 15 + Task 3 refactor: dispatch the panel's
          // derived corpus so the research worker routes to
          // ``LetterResearchService`` for ``fda_letters`` jobs.  The
          // value is derived from the coerced filters (the Document
          // Type pill the user actually sees inside the panel).
          corpus: submitCorpus,
          notify: notifyMe,
          // Task 8: attach the three letter-corpus filter arrays only
          // when the panel is dispatching a letter job.  Drug jobs omit
          // them entirely so the StartResearchRequest backend defaults
          // (``[]``) apply — matches the request shape from Task 6.
          ...(submitCorpus === 'fda_letters'
            ? {
                letter_types: coercedFilters.letterTypes,
                letter_corpus: coercedFilters.letterCorpus,
                letter_roles: coercedFilters.letterRoles,
                letter_divisions: coercedFilters.letterDivisions,
              }
            : {}),
          ...(submitCorpus === 'multi_review'
            && isUsReviewScope(coercedFilters.regions, isAdmin)
            ? {
                doc_types: normalizeSbaDocTypes(
                  coercedFilters.sbaDocTypes,
                ),
              }
            : {}),
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Failed to start research' }));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }

      const job = await res.json();
      setQuery('');
      navigate(`/research/${job.id}`);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  }, [query, maxResults, filters, panelCorpus, navigate, isAdmin, hasLabelNegotiations]);

  // Share
  const handleShare = useCallback(async (jobId: string) => {
    const url = `${window.location.origin}/research/${jobId}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, []);

  // Delete
  const handleDelete = useCallback(async (e: React.MouseEvent, jobId: string) => {
    e.stopPropagation();
    try {
      const token = await auth.currentUser?.getIdToken();
      await fetch(`${API_URL}/v2/research/${jobId}`, {
        method: 'DELETE',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...getImpersonationHeaders(),
        },
      });
      fetchJobs();
    } catch {
      // Silently fail
    }
  }, [fetchJobs]);

  return (
    <div className="max-w-6xl mx-auto px-4 pt-[15px] pb-6 space-y-6">
      {/* Compact brand mark — small logo + name on the Investigator surface.
          ``pl-2`` indents it by the logo↔name gap; 15px sits above (container
          pt) and below (the form's ``!mt-[15px]`` overrides the space-y gap so
          only the logo→form gap changes, not the rest of the panel). */}
      <div className="pl-2">
        <BrandMark size="sm" />
      </div>
      {/* Form */}
      <div className="relative !mt-[15px]">
        {/* Glow halo — matches Cogni's elevated treatment (SearchPage hero). */}
        <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl blur opacity-20" />
        <div className="relative bg-white rounded-xl shadow-xl shadow-slate-200/50 border border-slate-200/60 p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-1">
          Investigator (Deep Search)
        </h2>
        <p className="text-sm text-slate-500 mb-4">
          Surface deeply nuanced labeling precedents, edge cases, and rare
          matches across approved drug labels.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <textarea
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g., Are there situations where a drug gets approved for one broad indication and then later updates section 14 with additional studies but the indication does not change?"
            className="w-full px-4 py-3 rounded-lg border border-slate-300 text-sm
                       focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                       placeholder-slate-400 resize-none"
            rows={3}
            disabled={isSubmitting}
          />

          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-600">Max results:</label>
              <select
                value={maxResults}
                onChange={(e) => setMaxResults(Number(e.target.value))}
                className="px-3 py-1.5 rounded-md border border-slate-300 text-sm"
                disabled={isSubmitting}
              >
                {/* Letter and ci corpora expose wider options because
                    their retrieval is cheaper than label corpus (no
                    per-result LLM evaluator) and recall is sparser. */}
                {(filters.searchMode === 'fda_letters' || filters.searchMode === 'approval_letters'
                  ? [3, 5, 10, 20]
                  : filters.searchMode === 'ci'
                  ? [5, 10, 20, 50]
                  : [1, 3, 5]
                ).map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={notifyMe}
                onChange={(e) => setNotifyMe(e.target.checked)}
                disabled={isSubmitting}
              />
              Email me when done
            </label>

            <button
              type="submit"
              disabled={isSubmitting || query.trim().length < 10}
              className="px-6 py-2 bg-indigo-600 text-white text-sm font-medium
                         rounded-lg hover:bg-indigo-700 disabled:opacity-50
                         disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Starting...' : 'Start Deep Search'}
            </button>
          </div>
        </form>

        {error && (
          <div className="mt-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-md">{error}</div>
        )}
      </div>
      </div>

      {/* Filters — separate container below the search box, matching the Cogni
          and Keyword surfaces (filter row sits under the box, not inside it).
          ``panelCorpus`` is derived from the panel's own ``filters.searchMode``. */}
      <SearchFiltersComponent
        corpus={panelCorpus}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        isLoading={isSubmitting}
        surface="investigator"
      />

      {/* Job History */}
        {jobs.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200/60" data-testid="research-job-history">
          <div className="px-6 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-medium text-slate-700">Deep Search History</h3>
            <div className="flex items-center gap-2">
              {copied && <span className="text-xs text-green-600 font-medium">Link copied!</span>}
              <span className="text-xs text-slate-400">{jobs.length} job{jobs.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
          <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
            {jobs.map((job) => (
              <button
                key={job.id}
                onClick={() => openInNewTab(`/research/${job.id}`)}
                className="w-full text-left px-6 py-3 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm text-slate-800 line-clamp-2">{job.query}</p>
                  <div className="flex items-center gap-2 shrink-0">
                    {statusBadge(job.status)}
                    <span className="text-xs text-slate-400">{timeAgo(job.created_at)}</span>
                    {!['pending'].includes(job.status) && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleShare(job.id); }}
                        className="text-slate-300 hover:text-indigo-500 transition-colors p-0.5"
                        title="Copy share link"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                      </button>
                    )}
                    <button
                      onClick={(e) => handleDelete(e, job.id)}
                      className="text-slate-300 hover:text-red-500 transition-colors p-0.5"
                      title={['pending', 'running'].includes(job.status) ? 'Cancel' : 'Delete'}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                  {/* Corpus-aware counters (Task 15; ci added Plan-4 Task 5).
                     Backend mirrors ``drugs_*`` into ``letters_*`` only
                     when ``corpus == 'fda_letters'``, so we read the
                     matching pair of counters and label them accordingly.
                     ci has NO dedicated counter pair — it reuses
                     ``drugs_*`` directly (see the ``ResearchJob.corpus``
                     doc comment above) with a "document(s)" noun since a
                     ci job's rows are product pages / press releases, not
                     drugs.  We branch on ``job.corpus`` instead of
                     inspecting which counter is populated because the
                     letter mirror always populates both. */}
                  {job.corpus === 'fda_letters' ? (
                    <>
                      {job.status === 'complete' && job.letters_found != null && (
                        <span>{job.letters_found} letter match{job.letters_found !== 1 ? 'es' : ''}</span>
                      )}
                      {['running', 'analyzing', 'pending'].includes(job.status) && (
                        <>
                          <span>
                            Evaluating {job.letters_analyzed ?? 0}/{job.letters_total ?? 0} letters
                          </span>
                          {job.letters_found != null && job.letters_found > 0 && (
                            <span className="text-indigo-600 font-medium">
                              {job.letters_found} letter match{job.letters_found !== 1 ? 'es' : ''}
                            </span>
                          )}
                        </>
                      )}
                    </>
                  ) : job.corpus === 'ci' ? (
                    <>
                      {job.status === 'complete' && (
                        <span>{job.drugs_found} document match{job.drugs_found !== 1 ? 'es' : ''}</span>
                      )}
                      {['running', 'analyzing', 'pending'].includes(job.status) && (
                        <>
                          <span>{job.drugs_total > 0 ? `Evaluating ${job.drugs_analyzed}/${job.drugs_total} documents` : 'Finding documents…'}</span>
                          {job.drugs_found > 0 && (
                            <span className="text-indigo-600 font-medium">
                              {job.drugs_found} document match{job.drugs_found !== 1 ? 'es' : ''}
                            </span>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {job.status === 'complete' && (
                        <span>{job.drugs_found} drug match{job.drugs_found !== 1 ? 'es' : ''}</span>
                      )}
                      {['running', 'analyzing', 'pending'].includes(job.status) && (
                        <>
                          <span>{job.drugs_total > 0 ? `Evaluating ${job.drugs_analyzed}/${job.drugs_total} candidates` : 'Finding candidates…'}</span>
                          {job.drugs_found > 0 && (
                            <span className="text-indigo-600 font-medium">
                              {job.drugs_found} drug match{job.drugs_found !== 1 ? 'es' : ''}
                            </span>
                          )}
                        </>
                      )}
                    </>
                  )}
                  {job.status === 'error' && (
                    <span className="text-red-400">{job.error_message?.slice(0, 60)}</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
        )}
        {loadingJobs && jobs.length === 0 && (
          <div className="text-center text-sm text-slate-400 py-4">Loading deep search history...</div>
        )}
    </div>
  );
}
